import { useMemo } from 'react';

import { AssetDTO, compareFormSubmissionRevisions, FormDTO, FormElement, FormRevisionFieldStatus, FormSubmissionRevisionDTO } from '@gearbelt/global';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';

import { FormElementComponent } from '@/components/form/form-element';
import { FormRendererData } from '@/components/form/form-elements-renderer';
import CloseIcon from '@/components/icons/close';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';

type FormSubmissionRevisionDiffViewProps = {
  open: boolean;
  onClose: () => void;
  // The immediately-preceding revision (Before) and the selected revision (After). Revision 0 has no
  // predecessor and is not selectable, so both are always present when the modal is open.
  previous: FormSubmissionRevisionDTO;
  current: FormSubmissionRevisionDTO;
  elements: FormElement[];
  form: FormDTO;
  asset: AssetDTO;
};

const toFieldMap = (body: FormSubmissionRevisionDTO['data']): FormRendererData =>
  body.reduce<FormRendererData>((fieldMap, answer) => {
    if (answer?.elementId) {
      fieldMap[answer.elementId] = answer.value;
    }
    return fieldMap;
  }, {});

const formatRevisionDate = (isoDate: string) => dayjs(isoDate).format('MMM D, YYYY h:mm A');

export function FormSubmissionRevisionDiffView({ open, onClose, previous, current, elements, form, asset }: FormSubmissionRevisionDiffViewProps) {
  const previousForm = useForm<FormRendererData>({ values: toFieldMap(previous.data) });
  const currentForm = useForm<FormRendererData>({ values: toFieldMap(current.data) });

  const changedElements = useMemo(() => {
    const diff = compareFormSubmissionRevisions(previous.data, current.data);
    const changedIds = new Set(diff.fields.filter((field) => field.status !== FormRevisionFieldStatus.Unchanged).map((field) => field.elementId));
    return elements.filter((element) => changedIds.has(element.id));
  }, [previous.data, current.data, elements]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? undefined : onClose())}>
      <DialogContent
        onInteractOutside={(event) => event.preventDefault()}
        className="top-[6.5vh] flex h-[95vh] max-h-[95vh] w-[98.5vw] max-w-none flex-col overflow-x-hidden overflow-y-hidden p-0">
        {/* Fixed header — stays pinned while the field diffs scroll */}
        <div className="border-grey-2 flex shrink-0 flex-col gap-4 px-0 pt-4 pb-0">
          <div className="flex items-center justify-between gap-4 px-4">
            <DialogTitle hideCloseButton>
              <span className="text-lg font-normal">Showing changes:</span>
            </DialogTitle>
            <DialogClose aria-label="Close" className="cursor-pointer">
              <CloseIcon className="h-5 w-5" />
            </DialogClose>
          </div>

          <div className="border-b" />

          <div className="flex px-4">
            <div className="border-grey-2 min-w-0 flex-grow basis-0">
              <div className="mb-2 text-center text-base font-bold">Before</div>
              <div className="text-sm">
                <span className="font-bold">Form Version:</span> {previous.revision + 1}
              </div>
              <div className="text-sm">
                <span className="font-bold">Date:</span> {formatRevisionDate(previous.createdAt)}
              </div>
            </div>
            <div className="min-w-0 flex-grow basis-0 pl-6">
              <div className="mb-2 text-center text-base font-bold">After</div>
              <div className="text-sm">
                <span className="font-bold">Form Version:</span> {current.revision + 1}
              </div>
              <div className="text-sm">
                <span className="font-bold">Date:</span> {formatRevisionDate(current.createdAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4">
          <div className="border-b" />
        </div>

        {/* Scrollable field diffs */}
        <div className="scrollbar-persistent min-h-0 min-w-0 flex-1 overflow-y-auto px-4 pb-4">
          {changedElements.length === 0 ? (
            <div className="text-grey-5 p-4 text-sm">No changes in this revision.</div>
          ) : (
            changedElements.map((element) => {
              // The field name is shown once in the header below; blanking the per-side element's label
              // renders the value alone in each column (the element components hide an empty label).
              const valueElement: FormElement = { ...element, label: '' };
              return (
                <div key={element.id}>
                  <div className="bg-grey-1 py-1 text-center text-sm font-medium">
                    <span>{element.label}</span>
                  </div>
                  <div className="flex py-1">
                    <div className="border-grey-2 min-w-0 flex-grow basis-0 overflow-x-auto border-r-2 border-solid pr-6">
                      <FormProvider {...previousForm}>
                        <FormElementComponent element={valueElement} readonly isPreview={false} asset={asset} form={form} />
                      </FormProvider>
                    </div>
                    <div className="min-w-0 flex-grow basis-0 overflow-x-auto pl-6">
                      <FormProvider {...currentForm}>
                        <FormElementComponent element={valueElement} readonly isPreview={false} asset={asset} form={form} />
                      </FormProvider>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
